import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_SHORTCUT_BINDINGS,
  mergeShortcutBindings,
} from '@core/shortcuts/bindings.ts';
import { matchShortcut } from '@core/shortcuts/matchShortcut.ts';
import type { Settings } from '@core/settings/schema.ts';

export type UseSpeakingOptions = {
  enabled: boolean;
  settings: Settings;
  /** When false, keyboard shortcuts are ignored. */
  shortcutsEnabled?: boolean;
};

export type SpeakingErrorCode = 'secure' | 'permission' | 'unsupported' | 'unknown';

export type UseSpeakingResult = {
  isRecording: boolean;
  isInitializing: boolean;
  hasRecording: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  replayRecording: () => void;
  lastError: SpeakingErrorCode | null;
  clearError: () => void;
};

function stopStreamTracks(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}

/** iOS Safari prefers mp4/aac; Chromium prefers webm. */
function pickRecorderMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const candidates = [
    'audio/mp4',
    'audio/aac',
    'audio/webm;codecs=opus',
    'audio/webm',
  ];
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return undefined;
}

function classifyMediaError(err: unknown): SpeakingErrorCode {
  if (typeof window !== 'undefined' && !window.isSecureContext) return 'secure';
  const name = err && typeof err === 'object' && 'name' in err ? String((err as { name: string }).name) : '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') return 'permission';
  if (name === 'NotSupportedError' || name === 'TypeError') return 'unsupported';
  return 'unknown';
}

export function useSpeaking({
  enabled,
  settings,
  shortcutsEnabled = true,
}: UseSpeakingOptions): UseSpeakingResult {
  const [isRecording, setIsRecording] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [lastError, setLastError] = useState<SpeakingErrorCode | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeRef = useRef<string | undefined>(undefined);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const initRef = useRef(false);
  const recordingRef = useRef(false);
  /** Distinguishes hold (Alt+F) from toggle (Alt+R). */
  const gestureRef = useRef<'hold' | 'toggle' | null>(null);

  const clearError = useCallback(() => setLastError(null), []);

  const startRecording = useCallback(async () => {
    if (!enabled || recordingRef.current || initRef.current) {
      gestureRef.current = null;
      return;
    }

    if (!window.isSecureContext) {
      setLastError('secure');
      gestureRef.current = null;
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setLastError('unsupported');
      gestureRef.current = null;
      return;
    }

    clearError();
    setIsInitializing(true);
    initRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!initRef.current) {
        stopStreamTracks(stream);
        gestureRef.current = null;
        return;
      }
      streamRef.current = stream;
      const mimeType = pickRecorderMimeType();
      mimeRef.current = mimeType;
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blobType = mimeRef.current ?? 'audio/mp4';
        const blob = new Blob(chunksRef.current, { type: blobType });
        const url = URL.createObjectURL(blob);
        if (audioRef.current) {
          URL.revokeObjectURL(audioRef.current.src);
        }
        const audio = new Audio(url);
        audio.volume = settings.speakingPlaybackVolume;
        audioRef.current = audio;
        setHasRecording(true);
        void audio.play().catch(() => {
          // Autoplay may be blocked; Replay still works under a later gesture.
        });
      };
      recorder.start();
      recorderRef.current = recorder;
      recordingRef.current = true;
      setIsRecording(true);
    } catch (err) {
      setLastError(classifyMediaError(err));
      gestureRef.current = null;
    } finally {
      setIsInitializing(false);
      initRef.current = false;
    }
  }, [enabled, settings.speakingPlaybackVolume, clearError]);

  const stopRecording = useCallback(() => {
    if (!recordingRef.current || !recorderRef.current) {
      if (initRef.current) {
        initRef.current = false;
        setIsInitializing(false);
      }
      gestureRef.current = null;
      return;
    }
    try {
      recorderRef.current.stop();
    } catch {
      // already stopped
    }
    stopStreamTracks(streamRef.current);
    streamRef.current = null;
    recorderRef.current = null;
    recordingRef.current = false;
    gestureRef.current = null;
    setIsRecording(false);
  }, []);

  const replayRecording = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = settings.speakingPlaybackVolume;
    audio.currentTime = 0;
    void audio.play().catch(() => setLastError('unknown'));
  }, [settings.speakingPlaybackVolume]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = settings.speakingPlaybackVolume;
    }
  }, [settings.speakingPlaybackVolume]);

  useEffect(() => {
    if (!enabled || !shortcutsEnabled) return undefined;
    const bindings = mergeShortcutBindings(settings.shortcutBindings ?? DEFAULT_SHORTCUT_BINDINGS);

    const onKeyDown = (e: KeyboardEvent) => {
      if (matchShortcut(e, bindings.speakingRecord)) {
        e.preventDefault();
        if (!recordingRef.current && !initRef.current) {
          gestureRef.current = 'hold';
          void startRecording();
        }
        return;
      }
      if (matchShortcut(e, bindings.speakingRecordToggle)) {
        e.preventDefault();
        if (e.repeat) return;
        if (recordingRef.current || initRef.current) {
          stopRecording();
        } else {
          gestureRef.current = 'toggle';
          void startRecording();
        }
        return;
      }
      if (matchShortcut(e, bindings.speakingReplay)) {
        e.preventDefault();
        replayRecording();
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      // Keyboard hold-to-record (extension parity).
      if (matchShortcut(e, bindings.speakingRecord)) {
        e.preventDefault();
        if (gestureRef.current === 'hold') {
          stopRecording();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    window.addEventListener('keyup', onKeyUp, { capture: true });
    return () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true });
      window.removeEventListener('keyup', onKeyUp, { capture: true });
    };
  }, [
    enabled,
    shortcutsEnabled,
    settings.shortcutBindings,
    startRecording,
    stopRecording,
    replayRecording,
  ]);

  useEffect(() => {
    if (!enabled) {
      initRef.current = false;
      stopRecording();
      setLastError(null);
    }
  }, [enabled, stopRecording]);

  return {
    isRecording,
    isInitializing,
    hasRecording,
    startRecording,
    stopRecording,
    replayRecording,
    lastError,
    clearError,
  };
}
